/// <reference types="vite/client" />
import type { Command } from '@o2d/history';
import { withPatch } from '@o2d/shared';
import type { BoardState } from '../state/board-state.js';
import type { BoardObject, LineObject } from '../interfaces/board-object.interface.js';
import { collectAffectedLines } from './anchor-snapshot.helper.js';

interface ObjectSnapshot {
  readonly object: BoardObject;
  readonly index: number;
}

/**
 * Removes one or more objects and cascades anchor cleanup to any lines
 * referencing the removed targets. Snapshots the removed objects (with
 * insertion-order index for z-order preservation) and the original line
 * references, so `revert()` restores the exact pre-apply state.
 *
 * Apply ordering: anchor patches are emitted before removals so the
 * affected lines keep their last world-space coordinates without a
 * visual jump (their (x1,y1)/(x2,y2) are untouched; only the anchor
 * field is cleared).
 *
 * Per-line bundling: a line whose start- AND end-anchor both reference
 * removed targets receives a single `replaceObject` call patching both
 * fields at once — exactly one `object-updated` event.
 */
export class RemoveObjectWithAnchorsCommand implements Command<BoardState> {
  readonly label = 'Objekt löschen';

  /** Public read-only access to the ids passed at construction. */
  get ids(): ReadonlyArray<string> {
    return this._ids;
  }

  private targetSnapshots: ObjectSnapshot[] = [];
  private readonly lineSnapshots = new Map<string, LineObject>();

  /**
   * @param ids - IDs of objects to remove. Lines anchored to any of these are
   *   detected and patched automatically (cascade).
   * @param throwOnMissing - If true, `apply()` throws when an id is not found.
   *   Default false: unknown ids are skipped silently (DEV warns once per id).
   *   Duplicates in `ids` are processed idempotently regardless of this flag.
   */
  constructor(
    private readonly _ids: ReadonlyArray<string>,
    private readonly throwOnMissing = false,
  ) {
    if (import.meta.env.DEV && new Set(_ids).size !== _ids.length) {
      console.warn(
        '[RemoveObjectWithAnchorsCommand] Duplicate IDs in ids; ' +
          'duplicates will be processed idempotently.',
      );
    }
  }

  apply(state: BoardState): void {
    if (this._ids.length === 0) return;
    const targetSet = new Set(this._ids);

    this.lineSnapshots.clear();
    const affected = collectAffectedLines(state, targetSet);
    for (const lineId of affected.keys()) {
      const line = state.getObject(lineId) as LineObject | undefined;
      if (line) this.lineSnapshots.set(lineId, line);
    }

    this.targetSnapshots = [];
    const seen = new Set<string>();
    for (const id of this._ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      const obj = state.getObject(id);
      if (!obj) {
        if (this.throwOnMissing) throw new Error(`Object not found: ${id}`);
        if (import.meta.env.DEV) {
          console.warn(`[RemoveObjectWithAnchorsCommand] Unknown id: ${id}`);
        }
        continue;
      }
      this.targetSnapshots.push({ object: obj, index: state.getObjectIndex(id) });
    }

    // Phase 1: clear anchors on affected lines (one bundled update per line).
    for (const [lineId, changes] of affected) {
      const original = this.lineSnapshots.get(lineId);
      if (!original) continue;
      const patch: Partial<LineObject> = {};
      for (const change of changes) {
        if (change.endpoint === 'start') patch.startAnchor = undefined;
        else patch.endAnchor = undefined;
      }
      state.replaceObject(lineId, withPatch(original, patch));
    }

    // Phase 2: remove targets.
    for (const id of this._ids) state.removeObject(id);
  }

  revert(state: BoardState): void {
    // Restore targets first (with z-order), then reattach anchors.
    // Ascending index ensures each moveToIndex lands at the recorded slot.
    const sorted = [...this.targetSnapshots].sort((a, b) => a.index - b.index);
    for (const snap of sorted) {
      state.addObject(snap.object);
      state.moveToIndex(snap.object.id, snap.index);
    }
    for (const [lineId, original] of this.lineSnapshots) {
      state.replaceObject(lineId, original);
    }
  }
}
